import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { EndPointBurst } from "@/components/EndPointBurst";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

export const SUGGESTED_QUESTIONS = [
  "Who owes me money right now, ranked by how late they are?",
  "What do I owe this week, and what's overdue?",
  "Who are my top 5 suppliers by spend this quarter?",
  "Where did my money go last month, by category?",
];

// The Books agent has no receivables or payables tools, so it gets its own.
export const BOOKS_SUGGESTED_QUESTIONS = [
  "How is the business doing this year?",
  "Is anything blocking the books?",
  "What's my gross margin, and is it getting better?",
  "Are the books ready to close last month?",
];

function greeting(hour: number) {
  if (hour < 12) return "Good morning.";
  if (hour < 18) return "Good afternoon.";
  return "Good evening.";
}

/** Ticks once a minute, aligned to the minute boundary. */
function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(() => {
      setNow(new Date());
      interval = setInterval(() => setNow(new Date()), 60_000);
    }, 60_000 - (Date.now() % 60_000));
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);
  return now;
}

const dateFormat = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" });
const timeFormat = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" });

export function AgentWelcome({
  onAsk,
  disabled,
  questions = SUGGESTED_QUESTIONS,
}: {
  onAsk: (question: string) => void;
  disabled?: boolean;
  questions?: string[];
}) {
  const now = useNow();
  const reducedMotion = usePrefersReducedMotion();
  const reveal = (index: number) =>
    reducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 8 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.4, delay: 0.15 + index * 0.06, ease: [0.22, 1, 0.36, 1] as const },
        };

  return (
    <div className="flex min-h-full flex-col">
      <div className="relative min-h-40 flex-1">
        <EndPointBurst className="absolute inset-0" />
      </div>

      <div className="flex flex-col gap-6 pb-2 pt-4">
        <motion.div className="text-center" {...reveal(0)}>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            <time dateTime={now.toISOString()}>
              {dateFormat.format(now)} · {timeFormat.format(now)}
            </time>
          </p>
          <h3 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
            <span className="text-muted-foreground">{greeting(now.getHours())}</span>
            <br />
            What can I find for you?
          </h3>
        </motion.div>

        <ul className="flex flex-col gap-2" aria-label="Suggested questions">
          {questions.map((question, index) => (
            <motion.li key={question} {...reveal(index + 1)}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onAsk(question)}
                className="group flex w-full items-center gap-3 rounded-lg border bg-background/60 px-4 py-3 text-left text-sm backdrop-blur-sm transition-colors hover:border-foreground/25 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
              >
                <span className="flex-1">{question}</span>
                <ArrowRight
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                />
              </button>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  );
}
